import { FC, PropsWithChildren, ReactNode } from 'react';
import { Panel, Group, Separator, useDefaultLayout, type LayoutStorage } from 'react-resizable-panels';
import { Layout, LoadingSpinner } from '@repo/atomic-components';
import { Header } from './Header.tsx';
import { useAppSelector } from '@/store';
import { selectIsOnline } from '@/store/slices/app/networkStatus';

type ThreeColumnPdfTemplateProps = PropsWithChildren<{
  isDataLoading: boolean;
  actionsPanel: ReactNode;
  filterChipsPanel: ReactNode;
  contentLeftColumn: ReactNode;
  contentMiddleColumn: ReactNode;
  contentRightColumn: ReactNode;
}>;

const localStorageIdForSavingPanelWidth = 'PanelGroupWidth';

export const ThreeColumnPdfTemplate: FC<ThreeColumnPdfTemplateProps> = ({
  isDataLoading,
  actionsPanel,
  filterChipsPanel,
  contentLeftColumn,
  contentMiddleColumn,
  contentRightColumn,
}) => {
  const isOnline = useAppSelector(selectIsOnline);
  const { defaultLayout, onLayoutChange } = useDefaultLayout({
    id: localStorageIdForSavingPanelWidth,
    storage: (typeof window !== 'undefined' ? window.localStorage : undefined) as LayoutStorage,
  });

  return (
    <Layout>
      <div className="grid h-full w-full grid-cols-[1fr] grid-rows-[48px_0px_auto_auto_1fr] gap-0">
        <div className="order-0 col-span-full">
          <Header />
        </div>

        <div className="order-1 col-span-full content-center">
          {/** Todo: will be integrated when we have bundles list */}
          {/*<TopPageNavigation buttonLabel="Back to Case 4" handleBack={() => {}} />*/}
        </div>

        <div className="order-2 col-span-full">{actionsPanel}</div>

        <div className="order-3 col-span-full">{filterChipsPanel}</div>

        <div className="relative order-4 col-span-full h-full w-full overflow-hidden">
          {/* Offline overlay — sits on top of all panels, blocks all pointer events */}
          {!isOnline && <div className="absolute inset-0 z-50 cursor-not-allowed bg-black/50" />}
          {isDataLoading ? (
            <LoadingSpinner />
          ) : (
            <Group
              orientation="horizontal"
              id={localStorageIdForSavingPanelWidth}
              defaultLayout={defaultLayout}
              onLayoutChange={onLayoutChange}
            >
              <Panel minSize="20" defaultSize="30" maxSize="60" className="h-full w-full overflow-hidden">
                {contentLeftColumn}
              </Panel>
              <Separator className="bg-fletchers-white hover:bg-fletchers-gray-100 w-1.5" />
              <Panel minSize="0" defaultSize="15" maxSize="45" className="h-full w-full overflow-hidden">
                {contentMiddleColumn}
              </Panel>
              <Separator className="bg-fletchers-white hover:bg-fletchers-gray-100 border-r-fletchers-gray-110 w-1.5 border-r" />
              <Panel minSize="40" defaultSize="50" maxSize="85" className="relative h-full w-full overflow-hidden">
                {contentRightColumn}
              </Panel>
            </Group>
          )}
        </div>
      </div>
    </Layout>
  );
};
